import {AceLoggerService} from '@logging/ace-logger.service';
import {map} from 'rxjs/operators';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BaseService} from '@shared/services/api/base-service';
import {Observable, of} from 'rxjs';
import {Injectable} from '@angular/core';
import {Level, Zone, ZoneName} from '@app/graphql-typings/graphql';
import {AceLogger} from '@logging/ace-logger';
import {APIPageInfo, GRAPHQL_TYPES, GraphQlVariables, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {SiteId} from "@shared/services/api/access-sites.interface";
import * as gql from 'gql-query-builder';
import Fields from "gql-query-builder/build/Fields";


export interface LevelAndZoneConfigTracker {
  level_ids: Set<number>;
  zone_ids: Set<number>;
}

enum API_OPERATIONS {
  LEVEL = 'level',
  LEVELS = 'levels',
  ZONE = 'zone',
  ZONES = 'zones',
  ZONE_NAME = 'zone_name',
  ZONE_NAMES = 'zone_names'
}

interface LevelAPIDelete {
  level_id: number;
  deleted: boolean;
}

interface ZoneAPIDelete {
  zone_id: number;
  deleted: boolean;
}

interface ZoneNameAPIDelete {
  zone_name_id: number;
  deleted: boolean;
}


const thermalComfortConfigFields: Fields = [
  'ideal_relative_humidity_min',
  'ideal_relative_humidity_max',
  'ideal_relative_humidity_margin',
  'thermal_comfort_min_temp',
  'thermal_comfort_max_temp',
  'thermal_comfort_margin',
  'thermalcomfort_excluded',
  {
    thermalcomfort_schedule: [
      'day_start',
      'day_end',
      'exclude_weekends'
    ]
  }
];

const commonFields: Fields = ['site_id', 'system'];

const zoneNameFields: Fields = [
  ...commonFields,
  'zone_name_id',
  'zone_name'
];

const zoneFields: Fields = [
  ...commonFields,
  ...thermalComfortConfigFields,
  'zone_id',
  'level_id',
  'zone_name_id',
  {
    zone_name: zoneNameFields
  },
  'tenant_ids'
];


const levelFields: Fields = [
  ...commonFields,
  ...thermalComfortConfigFields,
  'level_id',
  'level_name',
  {zones: zoneFields},
  'tenant_ids'
];

const commonGraphqlVariables: GraphQlVariables = {
  site_id: {type: GRAPHQL_TYPES.INTEGER},
  site_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  system: {type: GRAPHQL_TYPES.BOOLEAN},
  start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
  limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
};


@Injectable({
  providedIn: 'root'
})
export class LevelZoneService extends BaseService {
  private readonly logger: AceLogger;
  private readonly headers: HttpHeaders;

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(LevelZoneService.name);
    this.headers = this.getHeaders();
  }

  private getAllItemsBySiteIds<T>(operation: API_OPERATIONS, fields: Fields, siteIds: SiteId[], system: boolean | null, pageSize: number = 10000): Observable<T> {
    const operationName = operation.valueOf();
    if (siteIds?.length) {
      const q = gql.query({
        operation: operationName,
        fields,
        variables: commonGraphqlVariables
      });
      const searchCriteria = {
        site_ids: siteIds,
        ...system != null && {system}
      };
      const fetchFn = (pageInfo: APIPageInfo) => this.httpUtilityService.graphQL<T>(q, operationName, {
        ...searchCriteria,
        start_index: pageInfo.first,
        limit: pageInfo.rows
      });
      return this.httpUtilityService.pageUntilComplete(fetchFn, {first: 0, rows: pageSize});
    } else {
      return of(<T>[]);
    }
  }

  getLevelsBySiteIds(siteIds: SiteId[], system = false): Observable<Level[]> {
    return this.getAllItemsBySiteIds(API_OPERATIONS.LEVELS, levelFields, siteIds, system);
  }

  getLevelsBySiteId(siteId: SiteId, system = false): Observable<Level[]> {
    return this.getLevelsBySiteIds(siteId != null ? [siteId] : null, system);
  }

  getZonesBySiteIds(siteIds: SiteId[], system = false): Observable<Zone[]> {
    return this.getAllItemsBySiteIds(API_OPERATIONS.ZONES, zoneFields, siteIds, system);
  }

  getZoneNamesBySiteIds(siteIds: SiteId[], system = false): Observable<ZoneName[]> {
    return this.getAllItemsBySiteIds(API_OPERATIONS.ZONE_NAMES, zoneNameFields, siteIds, system);
  }

  getZoneNamesBySiteId(siteId: SiteId, system = false): Observable<ZoneName[]> {
    return this.getZoneNamesBySiteIds(siteId != null ? [siteId] : null, system);
  }

  createLevels(levels: Level[]): Observable<Level[]> {
    if (levels.length > 0) {
      const options = {headers: this.getHeaders()};
      return this.http.post(this.getGateway() + '/levels', levels, options)
        .pipe(map((response: APIResponse<Level[]>) => this.httpUtilityService.extractResponseData(response, API_OPERATIONS.LEVELS)));
    } else {
      return of([]);
    }
  }

  createZones(zones: Zone[]): Observable<Zone[]> {
    if (zones.length > 0) {
      const options = {headers: this.getHeaders()};
      return this.http.post(this.getGateway() + '/zones', zones, options)
        .pipe(map((response: APIResponse<Zone[]>) => this.httpUtilityService.extractResponseData(response, API_OPERATIONS.ZONES)));
    } else {
      return of([]);
    }
  }

  createZoneNames(zoneNames: ZoneName[]): Observable<ZoneName[]> {
    if (zoneNames.length > 0) {
      const options = {headers: this.getHeaders()};
      return this.http.post(this.getGateway() + '/zone_names', zoneNames, options)
        .pipe(map((response: APIResponse<ZoneName[]>) => this.httpUtilityService.extractResponseData(response, API_OPERATIONS.ZONE_NAMES)));
    } else {
      return of([]);
    }
  }

  deleteLevel(levelId: number): Observable<number> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getGateway() + `/levels/${levelId}`, options)
      .pipe(map((response: APIResponse<LevelAPIDelete>) => this.httpUtilityService.extractResponseData<LevelAPIDelete>(response, API_OPERATIONS.LEVEL).level_id));
  }

  deleteZone(zoneId: number): Observable<number> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getGateway() + `/zones/${zoneId}`, options)
      .pipe(map((response: APIResponse<ZoneAPIDelete>) => this.httpUtilityService.extractResponseData<ZoneAPIDelete>(response, API_OPERATIONS.ZONE).zone_id));
  }

  deleteZoneName(zoneNameId: number): Observable<number> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getGateway() + `/zone_names/${zoneNameId}`, options)
      .pipe(map((response: APIResponse<ZoneNameAPIDelete>) => this.httpUtilityService.extractResponseData<ZoneNameAPIDelete>(response, API_OPERATIONS.ZONE_NAME).zone_name_id));
  }

  updateLevel(level: Level): Observable<Level> {
    const apiLevel: Level = Object.assign({}, level);
    delete apiLevel.zones;
    return this.http.put(this.getGateway() + '/levels/' + apiLevel.level_id, apiLevel, {headers: this.headers})
      .pipe(map((response: APIResponse<Level>) => this.httpUtilityService.extractResponseData(response, API_OPERATIONS.LEVEL)));
  }

  updateZone(zone: Zone): Observable<Zone> {
    return this.http.put(this.getGateway() + '/zones/' + zone.zone_id, zone, {headers: this.headers})
      .pipe(map((response: APIResponse<Zone>) => this.httpUtilityService.extractResponseData(response, API_OPERATIONS.ZONE)));
  }

  updateZoneName(zoneName: ZoneName): Observable<ZoneName> {
    const apiZoneName: ZoneName = Object.assign({}, zoneName);
    delete apiZoneName['zone_id'];
    const options = {headers: this.getHeaders()};
    return this.http.put(this.getGateway() + `/zone_names/${apiZoneName.zone_name_id}`, apiZoneName, options)
      .pipe(map((response: APIResponse<ZoneName>) => this.httpUtilityService.extractResponseData(response, API_OPERATIONS.ZONE_NAME)));
  }

  hasConfig(obj: Level | Zone) {
    return obj.thermal_comfort_min_temp != null
      || obj.thermal_comfort_max_temp != null
      || obj.thermal_comfort_margin != null
      || obj.thermalcomfort_excluded != null
      || obj.ideal_relative_humidity_min != null
      || obj.ideal_relative_humidity_max != null
      || obj.ideal_relative_humidity_margin != null
      || obj.thermalcomfort_schedule != null;
  }

  /**
   * Given a list of levels, reduce over all levels and zones, producing:
   *   configList: an array of levels and zones those have configs set against
   *   configTracker: an object to track if a level or zone has config by their id
   * @param levels
   */
  getConfigsFromLevels(levels: Level[]): {configList: Array<Level | Zone>, configTracker: LevelAndZoneConfigTracker} {
    const configTracker = {
      level_ids: new Set<number>(),
      zone_ids: new Set<number>()
    };
    const configList = levels.reduce((acc: Array<Level | Zone>, level: Level) => {
      if (this.hasConfig(level)) {
        acc.push(level);
        configTracker.level_ids.add(level.level_id);
      }
      if (level.zones) {
        level.zones.forEach((zone: Zone) => {
          if (this.hasConfig(zone)) {
            acc.push(zone);
            configTracker.zone_ids.add(zone.zone_id);
          }
        });
      }
      return acc;
    }, []);
    return {configList, configTracker};
  }

  /**
   * Given a list of levels, reduce over all levels and zones, producing:
   *   configList: an array of levels and zones those have configs set against
   *   configTracker: an object to track if a level or zone has config by their id
   * @param levelsOrZones
   */
  getConfigsFromLevelsOrZones(levelsOrZones: Array<Level | Zone>): {configList: Array<Level | Zone>, configTracker: LevelAndZoneConfigTracker} {
    const configTracker = {
      level_ids: new Set<number>(),
      zone_ids: new Set<number>()
    };
    const configList = levelsOrZones.reduce((acc: Array<Level | Zone>, levelOrZone: Level | Zone) => {
      if (this.hasConfig(levelOrZone)) {
        acc.push(levelOrZone);
        if (this.isZone(levelOrZone)) {
          configTracker.zone_ids.add((levelOrZone as Zone).zone_id);
        } else {
          configTracker.level_ids.add((levelOrZone as Level).level_id);
        }
      }
      return acc;
    }, []);
    return {configList, configTracker};
  }

  isZone(obj: any): obj is Zone {
    return obj['zone_name_id'] !== undefined && obj['level_id'] !== undefined;
  }

  isLevel(obj: any): obj is Level {
    return obj['level_id'] !== undefined && obj['zone_name_id'] === undefined;
  }

  private isPropsEqual(obj1: any, obj2: any, props: string[]): boolean {
    return obj1 == null && obj2 == null
      ? true
      : obj1 != null && obj2 != null
        ? props.every(p => obj1[p] === obj2[p])
        : false;
  }

  isConfigEqual(obj1: Level | Zone, obj2: Level | Zone): boolean {
    const SCHEDULE = 'thermalcomfort_schedule';
    const props = [
      'thermal_comfort_min_temp',
      'thermal_comfort_max_temp',
      'thermal_comfort_margin',
      'thermalcomfort_excluded',
      'ideal_relative_humidity_min',
      'ideal_relative_humidity_max',
      'ideal_relative_humidity_margin',
    ];

    const scheduleProps = obj1[SCHEDULE] != null
      ? Object.getOwnPropertyNames(obj1[SCHEDULE])
      : obj2[SCHEDULE] != null
        ? Object.getOwnPropertyNames(obj2[SCHEDULE])
        : [];

    return this.isPropsEqual(obj1, obj2, props) && this.isPropsEqual(obj1[SCHEDULE], obj2[SCHEDULE], scheduleProps);
  }

  private findLevelOrZoneFunction = (levelOrZone: Level | Zone, currentLevelOrZone: Level | Zone) => {
    if (this.isLevel(levelOrZone) && this.isLevel(currentLevelOrZone)) {
      return (currentLevelOrZone as Level).level_id === (levelOrZone as Level).level_id;
    } else if (this.isZone(levelOrZone) && this.isZone(currentLevelOrZone)) {
      return (currentLevelOrZone as Zone).zone_id === (levelOrZone as Zone).zone_id;
    } else {
      return undefined;
    }
  };

  findLevelOrZone(levelOrZone: Level | Zone, array: Array<Level | Zone>): Level | Zone | undefined {
    return array.find((currentLevelOrZone) => this.findLevelOrZoneFunction(levelOrZone, currentLevelOrZone));
  }

  findLevelOrZoneIndex(levelOrZone: Level | Zone, array: Array<Level | Zone>): number | undefined {
    return array.findIndex((currentLevelOrZone) => this.findLevelOrZoneFunction(levelOrZone, currentLevelOrZone));
  }


  populateZoneLookupMap(levels: Level[]): Map<number, Zone> {
    const zoneLookupByZoneId = new Map<number, Zone>();
    levels.forEach(level => {
      if (level.zones) {
        level.zones.forEach(zone => {
          if (zone.zone_id != null) {
            zone.level = level;
            zoneLookupByZoneId.set(zone.zone_id, zone);
          }
        });
      }
    });
    return zoneLookupByZoneId;
  }

}
